/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/subscriptionUsage.scss';

import React, {Fragment, MouseEventHandler, useMemo, useState} from "react";
import IDate from "@dvrd/idate";
import {AwesomeIcon, Loader} from "@dvrd/dvr-controls";

interface Props {
    onClickPortal: MouseEventHandler;
    currentPeriodStart: IDate;
    currentPeriodEnd: IDate;
    currentOrderCount: number;
    previousPeriods: Record<string, number>;
    loading: boolean;
}

const SHOW_MIN_PERIODS = 2; // Show min 2 previous periods

export default function SubscriptionUsage(props: Props) {
    const {onClickPortal, currentPeriodStart, currentPeriodEnd, currentOrderCount, previousPeriods, loading} = props;
    const [morePeriodsOpen, setMorePeriodsOpen] = useState(false);
    const hasMorePeriods = useMemo(() => {
        return Object.keys(previousPeriods).length > SHOW_MIN_PERIODS;
    }, [previousPeriods]);

    function onToggleMorePeriods() {
        setMorePeriodsOpen(!morePeriodsOpen);
    }

    function renderMinPreviousPeriods() {
        const keys = Object.keys(previousPeriods).slice(0, SHOW_MIN_PERIODS);
        if (!keys.length) return null;
        return renderPreviousPeriods(keys);
    }

    function renderMorePeriods() {
        if (!morePeriodsOpen || !hasMorePeriods) return null;
        const keys = Object.keys(previousPeriods).slice(SHOW_MIN_PERIODS);
        return renderPreviousPeriods(keys);
    }

    function renderPreviousPeriods(keys: Array<string>) {
        return keys.map((period: string, index: number) => (
            <Fragment key={index}>
                <label>{previousPeriods[period]}</label>
                <label>{period}</label>
                <label/>
            </Fragment>
        ));
    }


    return (
        <div className='subscription-block current-usage'>
            <Loader active={loading}/>
            <div className='header-container with-icon'>
                <AwesomeIcon name='chart-simple' className='header-icon'/>
                <label className='header-title'>Verbruik deze periode</label>
                <label className='sub-title'>Sinds {currentPeriodStart.format('DD-MM-YYYY') ?? '-'} tot
                    vandaag</label>
            </div>
            <div className='body-container'>
                <label className='bold'>Aantal orders</label>
                <label className='bold'>Deze periode</label>
                <label className='subscription-action' onClick={onClickPortal}>Facturen bekijken</label>
                <label>{currentOrderCount}</label>
                <label>{currentPeriodEnd.format('DD-MM-YYYY') ?? '-'} t/m {currentPeriodStart?.format(
                    'DD-MM-YYYY') ?? '-'}</label>
                <label/>
                {renderMinPreviousPeriods()}
                {renderMorePeriods()}
                {hasMorePeriods && <label className='subscription-action'
                                          onClick={onToggleMorePeriods}>{morePeriodsOpen ? 'Toon minder' :
                    'Toon meer'} vorige periodes</label>}
            </div>
        </div>
    );
}